/**
 * One poll timer per query key (UI overhaul PR-2, audit S2).
 *
 * TanStack Query schedules its `refetchInterval` timer PER OBSERVER
 * (`QueryObserver.#updateRefetchInterval`): three components reading the same
 * polled query with a 60 s cadence would fetch up to three times a minute,
 * offset by when each mounted. The rule this module enforces:
 *
 *   however many components read a polled query, exactly ONE of them runs
 *   the timer (the earliest still mounted — the "owner"), at the FASTEST
 *   cadence any of them currently asks for.
 *
 * When the owner unmounts the next member takes over, so a poll never stops
 * while somebody is still looking; a hidden tab is not a member at all, so it
 * neither owns nor slows a poll. `useApiQuery` does the bookkeeping — a
 * component never needs to know whether it is the owner.
 *
 * Cadences are `PollSpec`s: a number, or a function of the latest answer
 * (poll faster while a sync is in flight). Function specs must be STABLE
 * references (module-level, or memoized) — they are re-read after every
 * fetch, so they never need to be re-registered; only a changed NUMBER
 * re-arms the owner's timer immediately. The owner re-renders only when
 * ownership moves or the fastest numeric cadence changes — never because a
 * plain reader joined or left. A reader that joins with a FUNCTION spec is
 * therefore picked up at the owner's next tick, not at once: fine while every
 * function-spec key has one reader or readers sharing one spec (true today);
 * a future faster function spec should be a number, or accept one tick.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'

export type PollSpec<T> = number | ((data: T | undefined) => number | false)
type AnyPollSpec = number | ((data: unknown) => number | false)

type Member = { id: symbol; spec: AnyPollSpec }
type Room = { members: Member[]; listeners: Set<() => void>; version: number }

const rooms = new Map<string, Room>()

function notify(room: Room): void {
  room.version += 1
  for (const fn of room.listeners) fn()
}

/** The fastest NUMERIC cadence in the room, or null when none is numeric. */
function fastestNumber(room: Room): number | null {
  let best: number | null = null
  for (const m of room.members) {
    if (typeof m.spec === 'number' && m.spec > 0 && (best === null || m.spec < best)) best = m.spec
  }
  return best
}

/** Register `me` as a reader of the poll for `hash`; returns the leave fn. */
export function join(hash: string, me: symbol, spec: AnyPollSpec, onChange: () => void): () => void {
  let room = rooms.get(hash)
  if (!room) {
    room = { members: [], listeners: new Set(), version: 0 }
    rooms.set(hash, room)
  }
  const before = fastestNumber(room)
  room.members.push({ id: me, spec })
  room.listeners.add(onChange)
  // The first member becomes the owner (its snapshot flips); a later member
  // only matters to the owner when it asks for a faster numeric cadence.
  if (room.members.length === 1 || fastestNumber(room) !== before) notify(room)
  return () => {
    const r = rooms.get(hash)
    if (!r) return
    const wasOwner = r.members[0]?.id === me
    const beforeLeave = fastestNumber(r)
    r.members = r.members.filter((m) => m.id !== me)
    r.listeners.delete(onChange)
    if (r.members.length === 0) rooms.delete(hash)
    else if (wasOwner || fastestNumber(r) !== beforeLeave) notify(r)
  }
}

/** Update a member's cadence. A changed number re-arms the owner's timer now;
 *  a function is simply re-read at the next tick. */
export function setSpec(hash: string, me: symbol, spec: AnyPollSpec): void {
  const room = rooms.get(hash)
  const member = room?.members.find((m) => m.id === me)
  if (!room || !member || member.spec === spec) return
  const before = fastestNumber(room)
  member.spec = spec
  if (fastestNumber(room) !== before) notify(room)
}

export function isOwner(hash: string, me: symbol): boolean {
  return rooms.get(hash)?.members[0]?.id === me
}

/** The cadence the owner runs: the fastest any member asks for right now,
 *  given the latest answer; `false` when every member is paused. */
export function resolveInterval(hash: string, data: unknown): number | false {
  const room = rooms.get(hash)
  if (!room) return false
  let best: number | false = false
  for (const m of room.members) {
    const ms = typeof m.spec === 'function' ? m.spec(data) : m.spec
    if (ms === false || !(ms > 0)) continue
    if (best === false || ms < best) best = ms
  }
  return best
}

/**
 * Take part in the poll for `hash` while `wantsPoll` is true. Returns whether
 * THIS component should run the timer. Re-renders when ownership moves or
 * the fastest numeric cadence changes.
 */
export function usePollOwner<T>(hash: string, wantsPoll: boolean, spec: PollSpec<T> | undefined): boolean {
  // A stable identity per component instance (lazy state, not a ref: refs
  // are not readable during render under the react-hooks rules).
  const [me] = useState(() => Symbol('poll-observer'))
  const anySpec = (spec ?? false) as unknown as AnyPollSpec

  const subscribe = useCallback(
    (onChange: () => void) => (wantsPoll ? join(hash, me, anySpec, onChange) : () => {}),
    // The spec is deliberately NOT a dependency: re-joining on every cadence
    // change would hand ownership around; setSpec below updates it in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hash, wantsPoll, me],
  )
  const getSnapshot = useCallback(
    () => (wantsPoll && isOwner(hash, me) ? (rooms.get(hash)?.version ?? 0) : -1),
    [hash, wantsPoll, me],
  )
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (wantsPoll) setSpec(hash, me, anySpec)
  }, [hash, wantsPoll, me, anySpec])

  return snapshot >= 0
}

/** Test seams. */
export function _pollMembers(hash: string): number {
  return rooms.get(hash)?.members.length ?? 0
}
export function _pollVersion(hash: string): number {
  return rooms.get(hash)?.version ?? 0
}
export function _resetPollRoomsForTests(): void {
  rooms.clear()
}
